import type {
  DefaultEntityFilters,
  EntityFilter,
} from '@backstage/plugin-catalog-react';

import { useEntityList } from '@backstage/plugin-catalog-react';
import { notEmpty } from '@ipen-portal/plugin-common';
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { tss } from 'tss-react/mui';

const useStyles = tss.create(({ theme }) => ({
  formControl: {
    minWidth: 140,
  },
  menuItem: {
    color: 'black',
  },
  select: {
    backgroundColor: 'white',
  },
  toggleButton: {
    '&.Mui-selected': {
      '&:hover': {
        backgroundColor: theme.palette.primary.main,
      },
      'backgroundColor': theme.palette.primary.dark,
      'color': theme.palette.common.white,
    },
  },
  toggleButtonGroup: {
    backgroundColor: 'white',
  },
}));

type Props<
  Multiple extends false | true,
  Name extends string,
  FilterType extends EntityFilter,
  ValueType extends (Multiple extends true ? string[] : string),
> = {
  defaultValue?: Multiple extends true ? never : string;
  items: { label: string; value: string }[];
  label: string;
  mobile?: boolean;
  multiple?: Multiple;
  name: Name;
} & (
  | {
    Filter: new (v: ValueType) => FilterType;
  }
  | {
    filterFactory: (v: ValueType) => FilterType;
  }
);

export const SelectFilter = <
  Multiple extends false | true,
  Name extends string,
  FilterType extends EntityFilter,
  ValueType extends (Multiple extends true ? string[] : string),
  T extends DefaultEntityFilters & Partial<Record<Name, FilterType>>,
>(
  props: Props<Multiple, Name, FilterType, ValueType>,
) => {
  const { classes } = useStyles();
  const { defaultValue, items, label, mobile, multiple, name } = props;
  const Filter = 'Filter' in props ? props.Filter : undefined;
  const filterFactory =
    'filterFactory' in props ? props.filterFactory : undefined;
  const {
    queryParameters: { [name]: queryParameter },
    updateFilters,
  } = useEntityList<T>();

  const queryParameters = useMemo(
    () => [queryParameter ?? defaultValue].flat().filter(notEmpty) as string[],
    [queryParameter, defaultValue],
  );

  const [selectedOptions, setSelectedOptions] = useState(queryParameters);

  // Set selected options on query parameter updates; this happens at initial page load and from
  // external updates to the page location
  useEffect(() => {
    setSelectedOptions(queryParameters);
  }, [queryParameters]);

  const shouldAddFilter = selectedOptions.length > 0;

  useEffect(() => {
    if (!shouldAddFilter) {
      updateFilters({ [name]: undefined } as Partial<T>);
      return;
    }
    const arg = (multiple ? selectedOptions : selectedOptions[0]) as ValueType;
    updateFilters({
      [name]: Filter ? new Filter(arg) : filterFactory!(arg),
    } as Partial<T>);
  }, [
    name,
    shouldAddFilter,
    selectedOptions,
    Filter,
    filterFactory,
    updateFilters,
    multiple,
    defaultValue,
  ]);

  const updateSelection = (v: unknown) => {
    let value: string[] = [];
    if (Array.isArray(v)) {
      value = v;
    } else if (typeof v === 'string' && v.length) {
      value = selectedOptions.includes(v) ? [] : [v];
    }
    setSelectedOptions(value);
  };

  const labelId = `select-label-${name}`;
  return mobile
    ? (
        <FormControl
          className={classes.formControl}
          size="small"
          variant="outlined"
        >
          {!selectedOptions.length && <InputLabel id={labelId}>{label}</InputLabel>}
          <Select
            className={classes.select}
            labelId={selectedOptions.length ? undefined : labelId}
            multiple={multiple}
            onChange={e => {
              updateSelection(e.target.value);
            }}
            value={multiple ? selectedOptions : selectedOptions[0] ?? ''}
          >
            {!multiple && (
              <MenuItem className={classes.menuItem} value="">
                <em>No Filter</em>
              </MenuItem>
            )}

            {items.map(item => (
              <MenuItem
                className={classes.menuItem}
                key={item.value}
                value={item.value}
              >
                {item.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )
    : (
        <ToggleButtonGroup
          aria-label={label}
          className={classes.toggleButtonGroup}
          exclusive={!multiple}
          onChange={(_, v: string | string[]) => updateSelection(v)}
          sx={{ flexWrap: 'wrap' }}
          value={multiple ? selectedOptions : selectedOptions[0] ?? null}
        >
          {items.map(item => (
            <ToggleButton
              aria-label={item.label}
              className={classes.toggleButton}
              key={item.value}
              value={item.value}
            >
              {item.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      );
};
